// frontend_MainDashboard/src/hooks/api/useSettingsApi.ts

import { useCallback } from 'react';
import { useApiBase } from './useApiBase';
import {
  UserProfileResponse,
  UserProfileUpdateRequest,
  UploadProfilePictureResponse,
  ClientConfig,
} from '@openmirror/shared';

export interface SettingsApiHook {
  // Profile management
  getOperatorProfile: () => Promise<UserProfileResponse>;
  updateOperatorProfile: (payload: UserProfileUpdateRequest) => Promise<UserProfileResponse>;
  uploadProfilePicture: (file: File) => Promise<UploadProfilePictureResponse>;
  
  // AI Persona management
  fetchAiPersona: () => Promise<{ success: boolean; persona?: any; message?: string }>;
  updateAiPersona: (payload: { personaName?: string; personaBasePrompt?: string; personaCustomDescription?: string }) => Promise<{ success: boolean; persona?: any; message?: string }>;
  
  // Chatbot Settings management
  fetchChatbotSettings: () => Promise<{ success: boolean; settings?: any; message?: string }>;
  updateChatbotSettings: (payload: { triggerCooldownHours?: number }) => Promise<{ success: boolean; settings?: any; message?: string }>;
  
  // Client UI Settings management
  fetchClientUiSettings: () => Promise<{ success: boolean; settings?: any; message?: string }>;
  updateClientUiSettings: (payload: { showRandomProfilePictures?: boolean }) => Promise<{ success: boolean; settings?: any; message?: string }>;
  
  // Client General Config management
  fetchClientConfig: () => Promise<{ success: boolean; config?: ClientConfig; message?: string }>;
  updateClientConfig: (payload: ClientConfig) => Promise<{ success: boolean; config?: ClientConfig; message?: string }>;
}

export const useSettingsApi = (): SettingsApiHook => {
  const { makeRequest, makeFormDataRequest } = useApiBase();

  const getOperatorProfile = useCallback((): Promise<UserProfileResponse> => {
    return makeRequest<UserProfileResponse>('/client/profile', 'GET');
  }, [makeRequest]);

  const updateOperatorProfile = useCallback((payload: UserProfileUpdateRequest): Promise<UserProfileResponse> => {
    return makeRequest<UserProfileResponse, UserProfileUpdateRequest>('/client/profile', 'PUT', payload);
  }, [makeRequest]);

  const uploadProfilePicture = useCallback((file: File): Promise<UploadProfilePictureResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return makeFormDataRequest<UploadProfilePictureResponse>('/client/profile/upload-picture', 'POST', formData);
  }, [makeFormDataRequest]);

  const fetchAiPersona = useCallback((): Promise<{ success: boolean; persona?: any; message?: string }> => {
    return makeRequest<{ success: boolean; persona?: any; message?: string }>('/client/ai_persona', 'GET');
  }, [makeRequest]);

  const updateAiPersona = useCallback((payload: any): Promise<{ success: boolean; persona?: any; message?: string }> => {
    return makeRequest<{ success: boolean; persona?: any; message?: string }, any>('/client/ai_persona', 'PUT', payload);
  }, [makeRequest]);

  const fetchChatbotSettings = useCallback((): Promise<{ success: boolean; settings?: any; message?: string }> => {
    return makeRequest<{ success: boolean; settings?: any; message?: string }>('/client/chatbot_settings', 'GET');
  }, [makeRequest]);

  const updateChatbotSettings = useCallback((payload: any): Promise<{ success: boolean; settings?: any; message?: string }> => {
    return makeRequest<{ success: boolean; settings?: any; message?: string }, any>('/client/chatbot_settings', 'PUT', payload);
  }, [makeRequest]);

  const fetchClientUiSettings = useCallback((): Promise<{ success: boolean; settings?: any; message?: string }> => {
    return makeRequest<{ success: boolean; settings?: any; message?: string }>('/client/ui_settings', 'GET');
  }, [makeRequest]);

  const updateClientUiSettings = useCallback((payload: any): Promise<{ success: boolean; settings?: any; message?: string }> => {
    return makeRequest<{ success: boolean; settings?: any; message?: string }, any>('/client/ui_settings', 'PUT', payload);
  }, [makeRequest]);

  const fetchClientConfig = useCallback((): Promise<{ success: boolean; config?: ClientConfig; message?: string }> => {
    return makeRequest<{ success: boolean; config?: ClientConfig; message?: string }>('/client/config', 'GET');
  }, [makeRequest]);

  const updateClientConfig = useCallback((payload: ClientConfig): Promise<{ success: boolean; config?: ClientConfig; message?: string }> => {
    return makeRequest<{ success: boolean; config?: ClientConfig; message?: string }, ClientConfig>('/client/config', 'PUT', payload);
  }, [makeRequest]);

  return {
    getOperatorProfile,
    updateOperatorProfile,
    uploadProfilePicture,
    fetchAiPersona,
    updateAiPersona,
    fetchChatbotSettings,
    updateChatbotSettings,
    fetchClientUiSettings,
    updateClientUiSettings,
    fetchClientConfig,
    updateClientConfig,
  };
};
